// src/reports/ZeroSalesReport.jsx — Reporting → Location Reports → Zero Sales
// (Eric, 2026-09-15). Inventory sitting at a Teams / Warehouse location with
// zero sales from that location in the chosen date window (default: year to
// date — Eric, 2026-09-22), one row per SKU × location.
// Inventory = nightly snapshot (inventory_snapshot.py, "Refresh now" for
// admins); sales = realtime from the order cache.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, BlockStack, InlineStack, Text, Button, TextField, Banner, Badge, Checkbox, Modal } from '@shopify/polaris';
import { useAuthFetch, useAuth } from '../AuthComponent';
import { paneHeightToFitScroller, ReportDateRange, ReportPicker } from '../Reporting';

import { hasRole } from '../constants/roles';
const PAGE = 200;
// ET calendar dates for the sales window (the backend reads them as ET days).
const etDate = d => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const todayIso = () => etDate(new Date());
const jan1Iso = () => `${todayIso().slice(0, 4)}-01-01`;
const fmtDay = iso => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}/${y}`; };
const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
function fmtRun(ranAt) {
    if (!ranAt) return 'never';
    const d = new Date(ranAt);
    const t = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
    const day = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' });
    return `${t} ${day}`;
}
const STATUS_OPTIONS = [
    { label: 'All statuses', value: '' }, { label: 'Active', value: 'ACTIVE' }, { label: 'Unlisted', value: 'UNLISTED' },
    { label: 'Draft', value: 'DRAFT' }, { label: 'Archived', value: 'ARCHIVED' },
];
const TEAMS_ONLY_OPTIONS = [{ label: 'Team Only: all', value: '' }, { label: 'Team Only: yes', value: 'yes' }, { label: 'Team Only: no', value: 'no' }];
// No cell wraps (Eric, 2026-09-15): every cell is one line; a long product
// name widens the table and the pane scrolls sideways instead.
const COLUMNS = [
    { key: 'location',   label: 'Location',    width: 140 },
    { key: 'vendor',     label: 'Vendor',      width: 110 },
    { key: 'product',    label: 'Product' },
    { key: 'variant',    label: 'Variant',     width: 64 },
    { key: 'sku',        label: 'SKU',         width: 150 },
    { key: 'bin',        label: 'Bin',         width: 100 },
    { key: 'available',  label: 'Available',   width: 80, right: true },
    // 2026-09-20 (Eric): unit COGS (Shopify inventory-item cost) and retail
    // price, shown as units × unit; since 2026-10-27 the sort is that row value.
    { key: 'unit_cost',  label: 'Total COGS',   width: 96, right: true, tip: 'Available × unit cost (Shopify inventory item cost). Hover a value for the unit cost.' },
    { key: 'price',      label: 'Total Retail', width: 96, right: true, tip: 'Available × variant price in Shopify. Hover a value for the unit price.' },
    // 2026-10-28 (Eric): the variant's selling price; admins can edit it — it updates the price in Shopify.
    { key: 'unit_price', label: 'Price',        width: 110, right: true, tip: 'Selling price of the variant in Shopify. Admins: click to change it in Shopify.' },
    { key: 'teams_only', label: 'Team Only', width: 84, center: true },
    { key: 'status',     label: 'Status',      width: 84 },
];

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

export default function ZeroSalesReport() {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    const { storeSlug } = useParams();
    const storeKey = storeSlug || 'soccerpost';
    const [searchParams, setSearchParams] = useSearchParams();
    const [meta, setMeta] = useState({ locations: [], last_run: null, refreshing: false, ytd_start: null });
    const [locId, setLocId] = useState(searchParams.get('location') || '');
    const [product, setProduct] = useState(searchParams.get('product') || '');
    const [sku, setSku] = useState(searchParams.get('sku') || '');
    const [status, setStatus] = useState(searchParams.get('status') || '');
    const [teamsOnly, setTeamsOnly] = useState(searchParams.get('teams_only') || '');
    // 2026-10-28 (Eric): "Inventory not tracked" items (e.g. Number products) show by default; untick to leave them out.
    const [untracked, setUntracked] = useState(searchParams.get('untracked') !== '0');
    const untrackedQ = untracked ? '1' : '0';
    const [vendor, setVendor] = useState(searchParams.get('vendor') || '');
    const [dateFrom, setDateFrom] = useState(searchParams.get('from') || jan1Iso());
    const [dateTo, setDateTo] = useState(searchParams.get('to') || todayIso());
    const [sort, setSort] = useState({ key: searchParams.get('sort') || 'location', dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc' });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ rows: [], total: 0, total_available: 0, total_cost_value: 0, total_retail_value: 0, rows_without_cost: 0, shopify_product_base: '' });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const productQ = useDebounced(product, 400);
    const skuQ = useDebounced(sku, 400);
    const isAdmin = hasRole(user, ['admin']);
    // Price edit (2026-10-28, Eric): type a price → Check (reads Shopify's live price) → "Are you sure?" → Update.
    const [priceEdit, setPriceEdit] = useState(null);       // { variant_id, value }
    const [priceConfirm, setPriceConfirm] = useState(null); // dry-run answer from the server
    const [priceBusy, setPriceBusy] = useState(false);
    const [priceErr, setPriceErr] = useState(null);
    const [priceNote, setPriceNote] = useState(null);
    const postPrice = async (body) => {
        const r = await fetchAuth(`/api/reports/zero-sales/price?store=${storeKey}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        return j;
    };
    const checkPrice = async () => {
        if (!priceEdit) return;
        setPriceBusy(true); setPriceErr(null);
        try { setPriceConfirm(await postPrice({ variant_id: priceEdit.variant_id, price: priceEdit.value, dry_run: true })); }
        catch (e) { setPriceErr(e.message); }
        finally { setPriceBusy(false); }
    };
    const savePrice = async () => {
        setPriceBusy(true); setPriceErr(null);
        try {
            const j = await postPrice({ variant_id: priceConfirm.variant_id, price: priceConfirm.new_price, expected_price: priceConfirm.current_price });
            const p = Number(j.new_price);
            setData(d => ({ ...d, rows: d.rows.map(x => x.variant_id === j.variant_id ? { ...x, price: p } : x) }));
            setPriceNote(j.unchanged ? `${j.sku || j.product_title} is already $${j.new_price} in Shopify.` : `${j.sku || j.product_title}: price changed from $${j.current_price} to $${j.new_price} in Shopify.`);
            setPriceConfirm(null); setPriceEdit(null);
        } catch (e) { setPriceErr(e.message); }
        finally { setPriceBusy(false); }
    };
    const lastRunStatus = meta.last_run?.status;
    const pollRef = useRef(null);
    // Sticky filters + column header (Eric, 2026-09-17): the table is its own
    // scroll pane sized to the rest of the viewport, so the filter card above
    // it never scrolls away and the sticky <thead> pins inside the pane.
    const tableWrapRef = useRef(null);
    const [paneMaxH, setPaneMaxH] = useState(null);
    // 2026-09-22 (Eric): table runs to the bottom of the screen — drop the page's bottom gutter while this report is open.
    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);
    useLayoutEffect(() => {
        // 2026-09-21 (Eric): sized against the app's scroll container so the
        // page never scrolls; the pane is the only thing that does.
        const measure = () => { const el = tableWrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneMaxH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [meta.locations, err, lastRunStatus, paneMaxH, data.total, busy]);

    const loadMeta = useCallback(async () => {
        const r = await fetchAuth(`/api/reports/zero-sales/locations?store=${storeKey}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        setMeta(j);
        // 2026-09-22 (Eric): the picker and the URL carry the SHOPIFY location
        // id (same key the rest of the reports use). Legacy `?location=<row
        // id>` links upgrade in place; an id from another store is dropped.
        if (locId) {
            const byShopify = (j.locations || []).find(l => String(l.shopify_location_id) === String(locId));
            const byRow = (j.locations || []).find(l => String(l.location_id) === String(locId));
            if (!byShopify) setLocId(byRow ? String(byRow.shopify_location_id) : '');
        }
        return j;
    }, [fetchAuth, storeKey, locId]);

    const loadRows = useCallback(async () => {
        setBusy(true); setErr(null);
        try {
            const q = new URLSearchParams({ store: storeKey, location_ids: locId, product: productQ, sku: skuQ, status,
                                            teams_only: teamsOnly, untracked: untrackedQ, vendor, sort: sort.key, dir: sort.dir, page: String(page), page_size: String(PAGE),
                                            date_from: dateFrom, date_to: dateTo });
            const r = await fetchAuth(`/api/reports/zero-sales/rows?${q}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setData(j);
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }, [fetchAuth, storeKey, locId, productQ, skuQ, status, teamsOnly, untracked, vendor, sort, page, dateFrom, dateTo]);

    useEffect(() => { loadMeta().catch(e => setErr(e.message)); }, [loadMeta]);
    useEffect(() => { loadRows(); }, [loadRows]);
    useEffect(() => { setPage(1); }, [locId, productQ, skuQ, status, teamsOnly, untracked, vendor, sort, dateFrom, dateTo]);
    useEffect(() => {
        const p = new URLSearchParams(searchParams);
        const set = (k, v) => { if (v) p.set(k, v); else p.delete(k); };
        set('location', locId); set('product', product); set('sku', sku); set('status', status); set('teams_only', teamsOnly); set('vendor', vendor); set('untracked', untracked ? '' : '0');
        set('from', dateFrom === jan1Iso() ? '' : dateFrom); set('to', dateTo === todayIso() ? '' : dateTo);
        set('sort', sort.key === 'location' ? '' : sort.key); set('dir', sort.dir === 'asc' ? '' : sort.dir);
        if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
    }, [locId, product, sku, status, teamsOnly, untracked, vendor, sort, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

    // While a refresh runs, poll until the snapshot lands, then reload the rows.
    useEffect(() => {
        if (!meta.refreshing) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            try { const j = await loadMeta(); if (!j.refreshing) { clearInterval(pollRef.current); pollRef.current = null; loadRows(); } }
            catch (_e) { /* keep polling */ }
        }, 10000);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [meta.refreshing, loadMeta, loadRows]);

    const refreshNow = async () => {
        setErr(null);
        const r = await fetchAuth('/api/reports/zero-sales/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ store: storeKey }) });
        const j = await r.json();
        if (!r.ok) { setErr(j.detail || `HTTP ${r.status}`); return; }
        setMeta(m => ({ ...m, refreshing: true }));
    };

    const exportCsv = () => {
        const q = new URLSearchParams({ store: storeKey, location_ids: locId, product: productQ, sku: skuQ, status,
                                        teams_only: teamsOnly, untracked: untrackedQ, vendor, sort: sort.key, dir: sort.dir, date_from: dateFrom, date_to: dateTo });
        fetchAuth(`/api/reports/zero-sales/export.csv?${q}`).then(async r => {
            if (!r.ok) { let m = `HTTP ${r.status}`; try { m = (await r.json()).detail || m; } catch (_e) { /* not JSON */ } throw new Error(m); }
            return r.blob();
        }).then(b => {
            const a = document.createElement('a'); a.href = URL.createObjectURL(b);
            a.download = `zero_sales_${storeKey}_${dateFrom}_${dateTo}.csv`; a.click(); URL.revokeObjectURL(a.href);
        }).catch(e => setErr(e.message));
    };

    const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    const locOptions = useMemo(() => [{ label: 'All Teams & Warehouse locations', value: '' },
        ...meta.locations.map(l => ({ label: l.name, value: String(l.shopify_location_id) }))], [meta.locations]);
    const vendorOptions = useMemo(() => [{ label: 'All vendors', value: '' },
        ...(meta.vendors || []).map(v => ({ label: v, value: v }))], [meta.vendors]);
    const windowLabel = `${fmtDay(dateFrom)} – ${fmtDay(dateTo)}`;
    const lastRun = meta.last_run;
    const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE));
    const statusTone = s => (s === 'ACTIVE' ? 'success' : s === 'UNLISTED' ? 'info' : s === 'DRAFT' ? 'attention' : 'critical');

    // 2026-10-28 (Eric): this report runs on ONE store; under All Stores it says so plainly instead of loading.
    if (storeKey === 'all') {
        return (
            <Banner tone="critical" title="Zero Sales can't run across multiple stores">
                <p>It reads one store's inventory snapshot at a time. Pick a single store (Soccer Post, Surf, …) in the store menu at the top left.
                   To compare stock and sales across every store, use Location Reports → Cross Site Sales.</p>
            </Banner>
        );
    }
    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text as="h2" variant="headingMd">Zero Sales</Text>
                        <InlineStack gap="200" blockAlign="center">
                            <Text as="span" tone="subdued">Inventory as of {fmtRun(lastRun?.ran_at)}{lastRun?.status === 'failed' ? ' (last run failed)' : ''}</Text>
                            {isAdmin && <Button onClick={refreshNow} disabled={!!meta.refreshing} loading={!!meta.refreshing}>Refresh now</Button>}
                            <Button onClick={exportCsv} disabled={!data.total}>Export CSV</Button>
                        </InlineStack>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        Every SKU with available inventory at a Teams or Warehouse location that has not sold from that location between {windowLabel}.
                        A sale counts when a non-cancelled order placed in that window (ET) had a fulfillment order at the location carrying the SKU.
                        Inventory is the nightly Shopify snapshot; sales are live. Team Only = the product carries the "Team Only" tag;
                        Status = the Shopify product status (Active, Unlisted, Draft, Archived). Bin = the SKU's bin(s) from the nightly bin snapshot, where the location has one.
                        Items set to "Inventory not tracked" in Shopify (e.g. Number products) are included unless "Include untracked inventory" is unticked.
                    </Text>
                    {lastRun?.status === 'failed' && <Banner tone="warning">The last inventory snapshot failed: {lastRun.error}. Showing the previous snapshot.</Banner>}
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    {priceNote && <Banner tone="success" onDismiss={() => setPriceNote(null)}>{priceNote}</Banner>}
                    <InlineStack gap="300" blockAlign="end" wrap>
                        <ReportPicker label="Location" options={locOptions} value={locId} onChange={setLocId} searchable />
                        <div style={{ minWidth: 220 }}><TextField label="Product" labelHidden placeholder="Product name…" value={product} onChange={setProduct} autoComplete="off" clearButton onClearButtonClick={() => setProduct('')} /></div>
                        <div style={{ minWidth: 180 }}><TextField label="SKU" labelHidden placeholder="SKU…" value={sku} onChange={setSku} autoComplete="off" clearButton onClearButtonClick={() => setSku('')} /></div>
                        <ReportPicker label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
                        <ReportPicker label="Team Only" options={TEAMS_ONLY_OPTIONS} value={teamsOnly} onChange={setTeamsOnly} />
                        <ReportPicker label="Vendor" options={vendorOptions} value={vendor} onChange={setVendor} searchable />
                        <Checkbox label="Include untracked inventory" checked={untracked} onChange={setUntracked} />
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <Text as="span" tone="subdued">Sales window</Text>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} presets={['30d', '90d', 'ytd', 'last_ytd']} />
                    </InlineStack>
                    <InlineStack gap="400">
                        <Text as="span"><b>{fmtN(data.total)}</b> SKU / location rows</Text>
                        <Text as="span"><b>{fmtN(data.total_available)}</b> units available with zero sales</Text>
                        <Text as="span" title={data.rows_without_cost ? `${fmtN(data.rows_without_cost)} rows have no cost in Shopify and count $0 here` : 'Σ available × unit cost'}>
                            <b>{fmtUSD(data.total_cost_value)}</b> at COGS{data.rows_without_cost ? <span style={{ color: '#6d7175' }}> ({fmtN(data.rows_without_cost)} rows without a cost)</span> : null}
                        </Text>
                        <Text as="span" title="Σ available × retail price"><b>{fmtUSD(data.total_retail_value)}</b> at retail</Text>
                        {busy && <Text as="span" tone="subdued">Loading…</Text>}
                    </InlineStack>
                </BlockStack>
            </Card>
            <Card padding="0">
                <div ref={tableWrapRef} style={{ overflow: 'auto', maxHeight: paneMaxH || undefined,
                                 // clip the sticky header to the card's rounded box (2026-09-23, Eric)
                                 clipPath: 'inset(0 round 12px)' }}>
                    <table className="report-table" style={{ minWidth: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                            <tr>
                                {COLUMNS.map(c => (
                                    <th key={c.key} onClick={() => toggleSort(c.key)} title={c.tip || undefined}
                                        style={{ cursor: 'pointer', minWidth: c.width, textAlign: c.right ? 'right' : c.center ? 'center' : 'left', padding: '8px 8px', borderBottom: '1px solid #e1e3e5', whiteSpace: 'nowrap', userSelect: 'none' }}>
                                        {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r, i) => (
                                <tr key={`${r.shopify_location_id}:${r.variant_id || r.sku}:${i}`} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.location_name}</td>
                                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.vendor || '—'}</td>
                                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                                        {data.shopify_product_base && r.product_id
                                            ? <a href={`${data.shopify_product_base}${String(r.product_id).split('/').pop()}`} target="_blank" rel="noopener noreferrer" title="Open in Shopify admin">{r.product_title || '—'}</a>
                                            : (r.product_title || '—')}
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>{r.variant_title && r.variant_title !== 'Default Title' ? r.variant_title : ''}</td>
                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{r.sku || '—'}</td>
                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{r.bin || '—'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtN(r.available)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                        title={r.unit_cost != null ? `${fmtN(r.available)} × ${fmtUSD(r.unit_cost)} unit cost` : 'No cost on the Shopify inventory item'}>
                                        {r.unit_cost != null ? fmtUSD(r.available * r.unit_cost) : '—'}
                                    </td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                        title={r.price != null ? `${fmtN(r.available)} × ${fmtUSD(r.price)} unit price` : ''}>
                                        {r.price != null ? fmtUSD(r.available * r.price) : '—'}
                                    </td>
                                    <td style={{ padding: '4px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {isAdmin && r.variant_id && priceEdit?.variant_id === r.variant_id && !priceConfirm ? (
                                            <InlineStack gap="100" blockAlign="center" align="end" wrap={false}>
                                                <div style={{ width: 120 }} onKeyDown={e => { if (e.key === 'Enter') checkPrice(); if (e.key === 'Escape') setPriceEdit(null); }}>
                                                    <TextField label="New price" labelHidden prefix="$" type="text" inputMode="decimal" autoComplete="off" autoFocus
                                                               value={priceEdit.value} onChange={v => setPriceEdit(pe => ({ ...pe, value: v }))} />
                                                </div>
                                                <Button size="slim" variant="primary" onClick={checkPrice} loading={priceBusy}
                                                        disabled={!priceEdit.value || Number(priceEdit.value) === Number(r.price)}>Update</Button>
                                                <Button size="slim" variant="plain" onClick={() => { setPriceEdit(null); setPriceErr(null); }}>Cancel</Button>
                                            </InlineStack>
                                        ) : isAdmin && r.variant_id ? (
                                            <button type="button" title="Change the price in Shopify"
                                                    onClick={() => { setPriceErr(null); setPriceEdit({ variant_id: r.variant_id, value: r.price != null ? Number(r.price).toFixed(2) : '' }); }}
                                                    style={{ background: 'none', border: '1px dashed #c9cccf', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', font: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                                                {r.price != null ? fmtUSD(r.price) : '—'} ✎
                                            </button>
                                        ) : (r.price != null ? fmtUSD(r.price) : '—')}
                                        {priceErr && priceEdit?.variant_id === r.variant_id && !priceConfirm && <div style={{ color: '#d72c0d', fontSize: 12, whiteSpace: 'normal', maxWidth: 260, marginLeft: 'auto' }}>{priceErr}</div>}
                                    </td>
                                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>{r.teams_only ? <Badge tone="attention">Yes</Badge> : 'No'}</td>
                                    <td style={{ padding: '6px 10px' }}>{r.product_status ? <Badge tone={statusTone(r.product_status)}>{r.product_status.charAt(0) + r.product_status.slice(1).toLowerCase()}</Badge> : '—'}</td>
                                </tr>
                            ))}
                            {!busy && data.rows.length === 0 && (
                                <tr><td colSpan={COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>
                                    {lastRun ? 'No SKUs with inventory and zero sales match these filters.' : 'No inventory snapshot yet — run Refresh now or wait for the 4:30 AM ET snapshot.'}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {pages > 1 && (
                    <div style={{ padding: 10, borderTop: '1px solid #e1e3e5' }}>
                        <InlineStack align="space-between" blockAlign="center">
                            <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</Button>
                            <Text as="span" tone="subdued">Page {page} of {pages}</Text>
                            <Button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                        </InlineStack>
                    </div>
                )}
            </Card>
            {priceConfirm && (
                <Modal open title="Update the price in Shopify?" onClose={() => { if (!priceBusy) { setPriceConfirm(null); setPriceErr(null); } }}
                       primaryAction={{ content: `Yes, change to $${priceConfirm.new_price}`, onAction: savePrice, loading: priceBusy, destructive: true }}
                       secondaryActions={[{ content: 'Cancel', onAction: () => { setPriceConfirm(null); setPriceErr(null); }, disabled: priceBusy }]}>
                    <Modal.Section>
                        <BlockStack gap="300">
                            <Text as="p">Are you sure you want to update the price?</Text>
                            <Text as="p"><b>{priceConfirm.product_title}</b>{priceConfirm.variant_title && priceConfirm.variant_title !== 'Default Title' ? ` / ${priceConfirm.variant_title}` : ''} — SKU <span style={{ fontFamily: 'monospace' }}>{priceConfirm.sku || '—'}</span></Text>
                            <Text as="p" variant="headingMd">${priceConfirm.current_price} → ${priceConfirm.new_price}</Text>
                            <Text as="p" tone="subdued">
                                ${priceConfirm.current_price} is the price in Shopify right now. The new price applies to this variant everywhere it sells on {storeKey === 'soccerpost' ? 'Soccer Post' : storeKey} (every location and channel), not just this location.
                            </Text>
                            {priceConfirm.compare_at_price && <Banner tone="info">This variant has a compare-at price of ${Number(priceConfirm.compare_at_price).toFixed(2)}; it is left as it is.</Banner>}
                            {Number(priceConfirm.new_price) < Number(priceConfirm.current_price) * 0.5 && <Banner tone="warning">The new price is less than half the current price — double-check it.</Banner>}
                            {priceErr && <Banner tone="critical">{priceErr}</Banner>}
                        </BlockStack>
                    </Modal.Section>
                </Modal>
            )}
        </BlockStack>
    );
}
